import type { SenderAddress } from '../../types';
import type { SelectOption } from '../../constants/regions';

interface SenderAddressSectionProps {
  address: SenderAddress;
  regions: SelectOption[];
  onChange: (field: keyof SenderAddress, value: string) => void;
}

function SenderAddressSection({ address, regions, onChange }: SenderAddressSectionProps) {
  return (
    <div className="form-section">
      <h2>Sender Address</h2>
      <p className="section-description">
        This address will be used as the default sender (from) address for all shipments.
        EasyPost validates addresses against carrier databases.
      </p>

      <div className="form-grid-settings">
        <div className="form-group">
          <label htmlFor="sender-name">Name *</label>
          <input
            type="text"
            id="sender-name"
            value={address.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Full name or business name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sender-company">Company</label>
          <input
            type="text"
            id="sender-company"
            value={address.company}
            onChange={(e) => onChange('company', e.target.value)}
            placeholder="Company name (optional)"
          />
        </div>

        <div className="form-group full-width">
          <label htmlFor="sender-street1">Street Address *</label>
          <input
            type="text"
            id="sender-street1"
            value={address.street1}
            onChange={(e) => onChange('street1', e.target.value)}
            placeholder="Street address"
          />
        </div>

        <div className="form-group full-width">
          <label htmlFor="sender-street2">Address Line 2</label>
          <input
            type="text"
            id="sender-street2"
            value={address.street2}
            onChange={(e) => onChange('street2', e.target.value)}
            placeholder="Apt, suite, unit, etc. (optional)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sender-city">City *</label>
          <input
            type="text"
            id="sender-city"
            value={address.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="City"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sender-state">{address.country === 'CA' ? 'Province' : 'State'} *</label>
          <select
            id="sender-state"
            value={address.state}
            onChange={(e) => onChange('state', e.target.value)}
          >
            {regions.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="sender-zip">{address.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</label>
          <input
            type="text"
            id="sender-zip"
            value={address.zip}
            onChange={(e) => onChange('zip', e.target.value)}
            placeholder="ZIP code"
            maxLength={10}
          />
        </div>

        <div className="form-group">
          <label htmlFor="sender-country">Country</label>
          <select
            id="sender-country"
            value={address.country}
            onChange={(e) => onChange('country', e.target.value)}
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="sender-phone">Phone</label>
          <input
            type="tel"
            id="sender-phone"
            value={address.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="(555) 555-5555"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sender-email">Email</label>
          <input
            type="email"
            id="sender-email"
            value={address.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="email@example.com"
          />
        </div>
      </div>
    </div>
  );
}

export default SenderAddressSection;
